/**
 * REC-CONF-2: PO Exception Formatting Utility
 *
 * Centralized formatting for receiving exception indicators.
 * Advisory metadata only — does not affect lifecycle.
 */

/** Known exception types */
export type POExceptionType =
  | 'damaged'
  | 'refused'
  | 'missing'
  | 'carrier_issue'
  | 'supplier_issue'
  | 'mixed_condition';

/** Human-readable labels for each exception type */
const EXCEPTION_LABELS: Record<POExceptionType, string> = {
  damaged: 'Damaged freight',
  refused: 'Refused at dock',
  missing: 'Missing freight',
  carrier_issue: 'Carrier issue',
  supplier_issue: 'Supplier issue',
  mixed_condition: 'Mixed condition',
};

/** Helper explanations for each exception type */
export const EXCEPTION_EXPLANATIONS: Record<POExceptionType, string> = {
  damaged: 'Freight arrived but cannot be accepted as received inventory.',
  refused: 'Shipment rejected at dock and returned to carrier.',
  missing: 'Expected freight not present on delivery.',
  carrier_issue: 'Delivery problem caused by carrier handling or routing.',
  supplier_issue: 'Shipment discrepancy caused before dispatch.',
  mixed_condition: 'Multiple exception types apply to this delivery.',
};

/**
 * Format a PO exception type into a human-readable label.
 *
 * @param exceptionType - The exception type string
 * @returns Human-readable label like "Damaged freight", or null if no exception
 */
export function formatPOException(
  exceptionType?: string | null
): string | null {
  if (!exceptionType) return null;
  return EXCEPTION_LABELS[exceptionType as POExceptionType] || null;
}

/**
 * All selectable exception options for the UI.
 */
export const EXCEPTION_OPTIONS: { value: POExceptionType; label: string }[] = [
  { value: 'damaged', label: 'Damaged' },
  { value: 'refused', label: 'Refused' },
  { value: 'missing', label: 'Missing' },
  { value: 'carrier_issue', label: 'Carrier issue' },
  { value: 'supplier_issue', label: 'Supplier issue' },
  { value: 'mixed_condition', label: 'Mixed condition' },
];
